import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import FilterBar from "../../../../components/FilterNSearchBar/FilterNSearchBar";
import ItemDialog from "../components/ItemDialog";
import AddResourceDialog from "../components/AddResourceDialog";
import RefreshButton from "../../components/RefreshButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import { removeItem, upsertItem } from "../../hooks/useTrustCenterAdmin";
import {
  CARD_HEADER_SX,
  FILTER_BAR_WRAP_SX,
  countLabel,
  includesQuery,
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  RESOURCE_TYPES,
  VISIBILITY,
  groupByCategory,
  moveWithin,
  resourceLink,
  resourceSource,
  visibilityOptionsFor,
  normalizeVisibility,
} from "../../data/resources";
import usePresentFilter from "../../hooks/usePresentFilter";

const FILTERS = {
  Public: (r) => normalizeVisibility(r.visibility) === "public",
  Requestable: (r) => normalizeVisibility(r.visibility) === "requestable",
  "Email only": (r) => normalizeVisibility(r.visibility) === "email",
  Private: (r) => normalizeVisibility(r.visibility) === "private",
  "From Regodit": (r) => r.type === "knowledge_base",
  Uploaded: (r) => r.type === "document",
  Links: (r) => r.type === "link",
};

const columns = [
  { label: "", field: "drag", width: "4%" },
  { label: "Resource", field: "name", width: "42%" },
  { label: "Type", field: "type", width: "12%" },
  { label: "Visibility", field: "visibility", width: "20%" },
  { label: "Actions", field: "actions", width: "22%", align: "center" },
];

const EMPTY_CATEGORY = { name: "", description: "" };
// Stable empty form for the edit dialog's first render.
const EMPTY_RESOURCE = {
  name: "",
  description: "",
  categoryId: "",
  visibility: "public",
  url: "",
};

const categoryFields = [
  { name: "name", label: "Category name", type: "text", required: true },
  {
    name: "description",
    label: "Description",
    type: "text",
    helperText: "Optional. Shown under the category name.",
  },
];

const cardSx = (theme) => ({
  borderRadius: "12px",
  border: `1px solid ${theme.customColors.border.main}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
});

const ResourcesTab = ({
  categories,
  resources,
  pageUrl,
  onChange,
  onCopied,
  onDownload,
  focusResourceId = null,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  // The resource whose file is being fetched (its button spins).
  const [downloadingId, setDownloadingId] = useState(null);
  const download = async (row) => {
    setDownloadingId(row.id);
    try {
      await onDownload(row);
    } finally {
      setDownloadingId(null);
    }
  };
  const {
    filter,
    setFilter,
    options: filterOptions,
  } = usePresentFilter(resources, FILTERS);
  const [editingCategory, setEditingCategory] = useState(null);
  const [adding, setAdding] = useState(null); // { categoryId } while open
  const [editingResource, setEditingResource] = useState(null);
  const [removingResource, setRemovingResource] = useState(null);
  // The resource waiting for "Remove?" to be confirmed (row button).
  const [dragging, setDragging] = useState(null); // the row being dragged

  const matches = useMemo(
    () =>
      resources.filter(
        (r) =>
          (!filter || FILTERS[filter](r)) &&
          includesQuery(search, r.name, r.description, resourceSource(r))
      ),
    [resources, search, filter]
  );
  const isFiltering = !!search || !!filter;
  // While searching, hide categories with no match; otherwise show them all
  // so empty ones can be filled.
  const groups = groupByCategory(categories, matches, !isFiltering);
  const existingPolicyIds = useMemo(
    () => new Set(resources.filter((r) => r.policyId).map((r) => r.policyId)),
    [resources]
  );

  const setResource = (resource) =>
    onChange({ resources: upsertItem(resources, resource) });

  // Order is the order of the arrays: categories as listed, resources within
  // their category. The public page shows them in the same order.
  const moveResource = (id, categoryId, toIndex) =>
    onChange({
      resources: moveWithin(
        resources,
        id,
        toIndex,
        (r) => r.categoryId === categoryId
      ),
    });
  const moveCategory = (id, toIndex) =>
    onChange({ resourceCategories: moveWithin(categories, id, toIndex) });

  // A report opened from Compliance lands on this row.
  useEffect(() => {
    if (!focusResourceId) return undefined;
    document
      .getElementById(`admin-resource-${focusResourceId}`)
      ?.scrollIntoView?.({ block: "center" });
    return undefined;
  }, [focusResourceId]);

  // Drag-and-drop within a category. Off while searching or filtering, when
  // only part of the list is on screen.
  const rowDragProps = (row, rowIndex) =>
    isFiltering
      ? { id: `admin-resource-${row.id}` }
      : {
          id: `admin-resource-${row.id}`,
          draggable: true,
          onDragStart: (e) => {
            e.dataTransfer.effectAllowed = "move";
            setDragging(row);
          },
          onDragOver: (e) => {
            if (dragging?.categoryId === row.categoryId) e.preventDefault();
          },
          onDrop: (e) => {
            e.preventDefault();
            if (dragging?.categoryId === row.categoryId) {
              moveResource(dragging.id, row.categoryId, rowIndex);
            }
            setDragging(null);
          },
          onDragEnd: () => setDragging(null),
          sx: {
            cursor: "grab",
            opacity: dragging?.id === row.id ? 0.4 : 1,
          },
        };

  const validateCategory = (values) => {
    const name = values.name.trim().toLowerCase();
    const taken = categories.some(
      (c) => c.id !== values.id && c.name.trim().toLowerCase() === name
    );
    return taken ? { name: "A category with this name already exists." } : {};
  };

  const resourceFields = (resource) => [
    { name: "name", label: "Resource name", type: "text", required: true },
    {
      name: "description",
      label: "Description",
      type: "text",
      helperText: "One line, shown under the name.",
    },
    ...(resource?.type === "link"
      ? [{ name: "url", label: "URL", type: "text", required: true }]
      : []),
    {
      name: "categoryId",
      label: "Category",
      type: "select",
      required: true,
      options: categories.map((c) => ({ value: c.id, label: c.name })),
    },
    ...(resource?.type === "link"
      ? []
      : [
          {
            name: "visibility",
            label: "Visibility",
            type: "select",
            options: visibilityOptionsFor(resource?.type).map((value) => ({
              value,
              label: VISIBILITY[value].label,
            })),
            helperText: VISIBILITY[resource?.visibility]?.help,
          },
        ]),
  ];

  const renderCell = (row, column) => {
    switch (column.field) {
      case "drag":
        return isFiltering ? null : (
          <Tooltip title="Drag to reorder" arrow>
            <DragIndicatorIcon
              aria-label={`Drag to reorder ${row.name}`}
              sx={{ fontSize: 18, color: theme.customColors.icons.secondary }}
            />
          </Tooltip>
        );
      case "name":
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.customColors.text.secondary }}
            >
              {row.description ? `${row.description} · ` : ""}
              {resourceSource(row)}
            </Typography>
          </Box>
        );
      case "type":
        return (
          <Chip
            size="small"
            label={RESOURCE_TYPES[row.type].short}
            sx={{ backgroundColor: theme.customColors.background.light }}
          />
        );
      case "visibility":
        return row.type === "link" ? (
          <Tooltip title="Links are always public." arrow>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary, pl: 1 }}
            >
              Public
            </Typography>
          </Tooltip>
        ) : (
          <Select
            size="small"
            value={normalizeVisibility(row.visibility)}
            onChange={(e) =>
              setResource({ id: row.id, visibility: e.target.value })
            }
            SelectDisplayProps={{ "aria-label": `Visibility of ${row.name}` }}
            sx={{ width: 150, fontSize: 14 }}
          >
            {visibilityOptionsFor(row.type).map((value) => (
              <MenuItem key={value} value={value}>
                {VISIBILITY[value].label}
              </MenuItem>
            ))}
          </Select>
        );
      case "actions":
        return (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip
              title={
                row.visibility === "private"
                  ? "Private resources cannot be shared"
                  : "Copy link"
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  aria-label={`Copy link for ${row.name}`}
                  disabled={row.visibility === "private" || !pageUrl}
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      resourceLink(pageUrl, row.id)
                    );
                    onCopied?.();
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            {row.type === "link" ? (
              <Tooltip title="Open link" arrow>
                <IconButton
                  size="small"
                  aria-label={`Open ${row.name}`}
                  href={row.url}
                  target="_blank"
                  rel="noopener"
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip
                title={typeof row.id === "number" ? "Download" : "Saving…"}
                arrow
              >
                {/* Disabled until saved: a new row has no file id yet. */}
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Download ${row.name}`}
                    disabled={
                      !onDownload ||
                      typeof row.id !== "number" ||
                      downloadingId === row.id
                    }
                    onClick={() => download(row)}
                  >
                    {downloadingId === row.id ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Remove" arrow>
              <IconButton
                size="small"
                aria-label={`Remove ${row.name}`}
                onClick={() => setRemovingResource(row)}
                sx={{ color: theme.customColors.status.error.text }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit resource" arrow>
              <IconButton
                size="small"
                aria-label={`Edit ${row.name}`}
                onClick={() => setEditingResource(row)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      default:
        return row[column.field];
    }
  };

  return (
    <>
      <Card sx={cardSx(theme)}>
        <Box
          sx={{
            ...CARD_HEADER_SX,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              Resources
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <RefreshButton label="Resources" />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setEditingCategory(EMPTY_CATEGORY)}
                sx={outlinedButtonSx(theme)}
              >
                Add category
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={categories.length === 0}
                onClick={() => setAdding({ categoryId: null })}
                sx={primaryButtonSx(theme)}
              >
                Add resource
              </Button>
            </Stack>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors.text.secondary,
              lineHeight: 1.5,
            }}
          >
            Group documents and links into categories and drag them into the
            order you want. The first 4 in each category show on your Overview.
            Public ones open for anyone. Requestable ones need your approval on
            Access requests. Email only ones are emailed to whoever asks.
            Private ones never leave Regodit.
          </Typography>
        </Box>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Box sx={FILTER_BAR_WRAP_SX}>
            <FilterBar
              searchQuery={search}
              onSearchChange={(event) => setSearch(event.target.value)}
              filters={filterOptions}
              selectedFilter={filter}
              onFilterChange={setFilter}
              onClearFilters={() => {
                setSearch("");
                setFilter(null);
              }}
              countLabel={countLabel(matches.length, "resource")}
            />
          </Box>
          <Divider sx={{ borderColor: theme.customColors.border.main }} />
          {/* Categories are sections of this card, not cards inside it. */}
          <Box>
            {categories.length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  p: 2,
                  textAlign: "center",
                }}
              >
                No categories yet. Click Add category, for example
                “Certifications & reports” or “Policies”.
              </Typography>
            )}
            {categories.length > 0 && groups.length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  p: 2,
                  textAlign: "center",
                }}
              >
                No resources match.
              </Typography>
            )}
            {groups.map((group, groupIndex) => (
              <Box
                key={group.id}
                component="section"
                aria-label={group.name}
                sx={{
                  borderBottom: `1px solid ${theme.customColors.border.main}`,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 2, pt: 2, pb: 1.25 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: theme.customColors.text.strong,
                      }}
                    >
                      {group.name}{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: theme.customColors.text.secondary }}
                      >
                        ({group.resources.length})
                      </Typography>
                    </Typography>
                    {group.description && (
                      <Typography
                        variant="caption"
                        sx={{ color: theme.customColors.text.secondary }}
                      >
                        {group.description}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setAdding({ categoryId: group.id })}
                    sx={{ textTransform: "none" }}
                    aria-label={`Add resource to ${group.name}`}
                  >
                    Add
                  </Button>
                  {!isFiltering && (
                    <>
                      <Tooltip title="Move up" arrow>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Move ${group.name} up`}
                            disabled={groupIndex === 0}
                            onClick={() =>
                              moveCategory(group.id, groupIndex - 1)
                            }
                          >
                            <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down" arrow>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Move ${group.name} down`}
                            disabled={groupIndex === groups.length - 1}
                            onClick={() =>
                              moveCategory(group.id, groupIndex + 1)
                            }
                          >
                            <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Edit category" arrow>
                    <IconButton
                      size="small"
                      aria-label={`Edit category ${group.name}`}
                      onClick={() =>
                        setEditingCategory(
                          categories.find((c) => c.id === group.id)
                        )
                      }
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {group.resources.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.customColors.text.secondary, p: 2 }}
                  >
                    Nothing here yet. Click Add to put a resource in this
                    category.
                  </Typography>
                ) : (
                  <ReusableTable
                    columns={columns}
                    data={group.resources}
                    renderCell={renderCell}
                    getRowProps={rowDragProps}
                    showPagination={false}
                  />
                )}
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <ItemDialog
        open={!!editingCategory}
        title={editingCategory?.id ? "Edit category" : "Add category"}
        fields={categoryFields}
        initialValues={editingCategory}
        emptyValues={EMPTY_CATEGORY}
        validate={validateCategory}
        onClose={() => setEditingCategory(null)}
        onSave={(values) =>
          onChange({
            resourceCategories: upsertItem(categories, {
              ...values,
              name: values.name.trim(),
              description: (values.description || "").trim(),
            }),
          })
        }
        onRemove={(id) =>
          onChange({ resourceCategories: removeItem(categories, id) })
        }
        removeHelperText="Removes this empty category from your Resources."
        removeBlockedReason={
          editingCategory &&
          resources.some((r) => r.categoryId === editingCategory.id)
            ? "To remove this category, first move its resources to another category or remove them."
            : null
        }
      />

      <ItemDialog
        open={!!editingResource}
        title="Edit resource"
        fields={resourceFields(editingResource)}
        initialValues={editingResource}
        emptyValues={EMPTY_RESOURCE}
        validate={(values) =>
          values.type === "link" &&
          !/^https?:\/\/\S+$/.test((values.url || "").trim())
            ? { url: "Enter a link that starts with https://" }
            : {}
        }
        onClose={() => setEditingResource(null)}
        onSave={(values) =>
          setResource({
            ...values,
            name: values.name.trim(),
            description: (values.description || "").trim(),
            ...(values.type === "link"
              ? { url: values.url.trim(), visibility: "public" }
              : {}),
          })
        }
        onRemove={(id) => onChange({ resources: removeItem(resources, id) })}
        removeHelperText="It will no longer appear on your Trust Center. Visitors who already downloaded it keep their copy."
      />

      <ConfirmDialog
        open={!!removingResource}
        onClose={() => setRemovingResource(null)}
        onConfirm={() => {
          onChange({ resources: removeItem(resources, removingResource.id) });
          setRemovingResource(null);
        }}
        heading={`Remove ${removingResource?.name || "resource"}?`}
        content="It will no longer appear on your Trust Center. Visitors who already downloaded it keep their copy."
        buttonText="Remove"
      />

      <AddResourceDialog
        open={!!adding}
        categories={categories}
        initialCategoryId={adding?.categoryId}
        existingPolicyIds={existingPolicyIds}
        onClose={() => setAdding(null)}
        onAdd={(newResources) =>
          onChange({
            resources: newResources.reduce(
              (list, r) => upsertItem(list, r),
              resources
            ),
          })
        }
      />
    </>
  );
};

export default ResourcesTab;
